import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, ArrowLeft, Loader, KeyRound } from "lucide-react";
import { BASE_URL, forgotPasswordApi, verifyOtpApi } from "../../../api/api";
import { toast } from "react-toastify";
import axios from "axios";

const ForgotPassword = () => {
    const [step, setStep] = useState(1);
    const [email, setEmail] = useState("");
    const [otp, setOtp] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    const handleEmailChange = (e) => {
        const { value } = e.target;
        const noSpaceValue = value.replace(/\s/g, "");
        setEmail(noSpaceValue);
    };

    const handleOtpChange = (e) => {
        const { value } = e.target;
        const sanitizedValue = value.replace(/\D/g, "").slice(0, 6);
        setOtp(sanitizedValue);
    };

    const handleKeyDown = (e) => {
        if (e.key === " ") e.preventDefault();
    };

    const handleSendOtp = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const response = await axios.post(
                `${BASE_URL}${forgotPasswordApi}?email=${encodeURIComponent(email)}`,
                {},
                {
                    headers: {
                        Accept: "application/json",
                    },
                }
            );

            const resData = response.data;

            if (resData.status) {
                setStep(2);
                toast.success(resData.message || "OTP sent to your email!");
            } else {
                toast.error(resData.message || "Failed to send OTP. Please try again.");
            }
        } catch (error) {
            const message =
                error.response?.data?.message ||
                "Something went wrong. Please try again later.";
            toast.error(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const response = await axios.post(
                `${BASE_URL}${verifyOtpApi}?email=${encodeURIComponent(email)}&otp=${otp}`,
                {},
                {
                    headers: {
                        Accept: "application/json",
                    },
                }
            );

            const resData = response.data;

            if (resData.status) {
                setIsSuccess(true);
                toast.success(resData.message || "New password sent to your email!");
            } else {
                toast.error(resData.message || "Invalid OTP. Please try again.");
            }
        } catch (error) {
            const message =
                error.response?.data?.message ||
                "Something went wrong. Please try again later.";
            toast.error(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="w-full max-w-md"
            >
                <div className="bg-white rounded-2xl shadow-xl p-8">
                    <div className="text-center mb-8">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-100 rounded-full mb-4">
                            {step === 1 ? (
                                <Mail className="w-8 h-8 text-yellow-600" />
                            ) : (
                                <KeyRound className="w-8 h-8 text-yellow-600" />
                            )}
                        </div>
                        <h1 className="text-2xl font-bold text-neutral-900">
                            {step === 1 ? "Forgot Password?" : "Verify OTP"}
                        </h1>
                        <p className="text-gray-500 mt-2 text-sm">
                            {step === 1
                                ? "Enter your email to receive OTP."
                                : `Enter the OTP sent to ${email}`}
                        </p>
                    </div>

                    {isSuccess ? (
                        <div className="text-center space-y-4">
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full">
                                <svg
                                    className="w-8 h-8 text-green-600"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M5 13l4 4L19 7"
                                    />
                                </svg>
                            </div>
                            <p className="text-gray-600 text-sm">
                                A new password has been sent to{" "}
                                <span className="font-semibold text-neutral-900">
                                    {email}
                                </span>
                            </p>
                            <p className="text-gray-500 text-xs">
                                Check your inbox and spam folder.
                            </p>
                            <Link
                                to="/login"
                                className="inline-block w-full py-3.5 bg-neutral-900 hover:bg-yellow-400 hover:text-neutral-900 text-white font-bold rounded-xl shadow-sm transition-all duration-300"
                            >
                                Go to Sign In
                            </Link>
                        </div>
                    ) : (
                        <>
                            {step === 1 && (
                                <form onSubmit={handleSendOtp} className="space-y-6">
                                    <div>
                                        <label
                                            htmlFor="email"
                                            className="block text-sm font-bold text-neutral-900 mb-2"
                                        >
                                            Email Address{" "}
                                            <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                                                <Mail size={18} />
                                            </div>
                                            <input
                                                id="email"
                                                type="email"
                                                name="email"
                                                value={email}
                                                onKeyDown={handleKeyDown}
                                                onChange={handleEmailChange}
                                                placeholder="Enter your email"
                                                required
                                                className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-neutral-900 focus:outline-none focus:bg-white focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-all placeholder:text-gray-400"
                                            />
                                        </div>
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="w-full py-3.5 bg-neutral-900 hover:bg-yellow-400 hover:text-neutral-900 text-white font-bold rounded-xl shadow-sm transition-all duration-300 group disabled:opacity-70 disabled:cursor-not-allowed cursor-pointer"
                                    >
                                        {isSubmitting ? (
                                            <span className="flex items-center justify-center">
                                                <Loader className="animate-spin -ml-1 mr-2 h-4 w-4" />
                                                Sending OTP...
                                            </span>
                                        ) : (
                                            "Send OTP"
                                        )}
                                    </button>
                                </form>
                            )}

                            {step === 2 && (
                                <form onSubmit={handleVerifyOtp} className="space-y-6">
                                    <div>
                                        <label
                                            htmlFor="otp"
                                            className="block text-sm font-bold text-neutral-900 mb-2"
                                        >
                                            Enter OTP{" "}
                                            <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                                                <KeyRound size={18} />
                                            </div>
                                            <input
                                                id="otp"
                                                type="text"
                                                name="otp"
                                                value={otp}
                                                onKeyDown={handleKeyDown}
                                                onChange={handleOtpChange}
                                                placeholder="Enter 6-digit OTP"
                                                required
                                                maxLength={6}
                                                className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-neutral-900 tracking-widest focus:outline-none focus:bg-white focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-all placeholder:text-gray-400 placeholder:tracking-normal"
                                            />
                                        </div>
                                        <p className="text-xs text-gray-500 mt-2">
                                            Check your email for the 6-digit OTP
                                        </p>
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={isSubmitting || otp.length < 6}
                                        className="w-full py-3.5 bg-neutral-900 hover:bg-yellow-400 hover:text-neutral-900 text-white font-bold rounded-xl shadow-sm transition-all duration-300 group disabled:opacity-70 disabled:cursor-not-allowed cursor-pointer"
                                    >
                                        {isSubmitting ? (
                                            <span className="flex items-center justify-center">
                                                <Loader className="animate-spin -ml-1 mr-2 h-4 w-4" />
                                                Verifying...
                                            </span>
                                        ) : (
                                            "Verify OTP & Send Password"
                                        )}
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => {
                                            setStep(1);
                                            setOtp("");
                                        }}
                                        className="w-full py-3.5 bg-gray-100 hover:bg-gray-200 text-neutral-900 font-bold rounded-xl shadow-sm transition-all duration-300 cursor-pointer"
                                    >
                                        Change Email
                                    </button>
                                </form>
                            )}
                        </>
                    )}

                    {!isSuccess && (
                        <div className="mt-8 pt-6 border-t border-gray-100 text-center">
                            <Link
                                to="/login"
                                className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-neutral-900 transition-colors"
                            >
                                <ArrowLeft size={16} className="mr-2" />
                                Back to Sign In
                            </Link>
                        </div>
                    )}
                </div>
            </motion.div>
        </div>
    );
};

export default ForgotPassword;

